import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { AlertCircle, LogIn } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';

export const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const { login, isLoading, user } = useAuth();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);

  // Redirect if already logged in
  React.useEffect(() => {
    if (user) {
      navigate('/');
    }
  }, [user, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!username || !password) {
      setError('Please enter both username and password');
      return;
    }

    const success = await login(username, password);
    if (success) {
      navigate('/');
    } else {
      setError('Invalid credentials');
    }
  };

  return (
    <div className="min-h-screen app-bg flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        className="w-full max-w-md"
      >
        {/* Header */}
        <div className="text-center mb-8">
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.1, type: 'spring' }}
            className="inline-block mb-4"
          >
            <div className="w-16 h-16 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center">
              <LogIn className="w-8 h-8 text-white" />
            </div>
          </motion.div>
          <h1 className="text-3xl font-bold text-white mb-2">StarryScout</h1>
          <p className="text-gray-400">FRC 2026 REBUILT™ Scouting Platform</p>
        </div>

        {/* Login Form */}
        <motion.form
          onSubmit={handleSubmit}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2 }}
          className="glass p-8"
        >
          {error && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="flex items-center gap-3 p-3 bg-red-500/10 border border-red-500/30 rounded-lg mb-6 text-red-300"
            >
              <AlertCircle className="w-5 h-5 flex-shrink-0" />
              <p className="text-sm">{error}</p>
            </motion.div>
          )}

          {/* Username Input */}
          <div className="mb-4">
            <label className="form-label">Username</label>
            <input
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder="admin, scout, or strategist"
              disabled={isLoading}
              className="form-input"
            />
          </div>

          {/* Password Input */}
          <div className="mb-6">
            <label className="form-label">Password</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              disabled={isLoading}
              className="form-input"
            />
          </div>

          {/* Login Button */}
          <motion.button
            type="submit"
            disabled={isLoading}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            className="w-full btn btn-primary btn-lg mb-4"
          >
            {isLoading ? 'Logging in...' : 'Login'}
          </motion.button>

          {/* Demo Credentials */}
          <div className="pt-6 border-t border-gray-700">
            <p className="text-xs text-gray-400 mb-3 font-semibold">Demo Credentials:</p>
            <div className="space-y-2 text-xs text-gray-300">
              <div className="flex justify-between">
                <span>Admin:</span>
                <code className="bg-dark-900/50 px-2 py-1 rounded">admin / admin123</code>
              </div>
              <div className="flex justify-between">
                <span>Scout:</span>
                <code className="bg-dark-900/50 px-2 py-1 rounded">scout / scout123</code>
              </div>
              <div className="flex justify-between">
                <span>Strategist:</span>
                <code className="bg-dark-900/50 px-2 py-1 rounded">strategist / strat123</code>
              </div>
            </div>
          </div>
        </motion.form>

        <div className="flex flex-col gap-3 mt-4">
          <Link to="/signup" className="w-full btn btn-secondary btn-lg text-center">
            Create Account
          </Link>
          <Link to="/browse" className="w-full btn btn-secondary btn-lg text-center">
            View as Guest
          </Link>
        </div>

        {/* Footer */}
        <p className="text-center text-xs text-gray-500 mt-6">
          Production-ready scouting platform for competitive FRC teams
        </p>
      </motion.div>
    </div>
  );
};
